import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DomainDataService } from '../../../../../../../services';
import { ReadonlyAccessWidget } from '../../../../../../../ui/widget';
import { AcceleratorAreaService } from '../../../../accelerator-area.service';

@Component({
	selector: 'topics-configure-topic-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		ReadonlyAccessWidget,
		DropDownsModule,
		FormsModule
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './configure-topic.dialog.html',
})
export class TopicsConfigureTopicDialog {

	public dialog: DialogContext<{ accTopicId: number }> = inject(MAT_DIALOG_DATA);
	public dialogRef = inject(MatDialogRef<TopicsConfigureTopicDialog>);

	private dialogService = inject(DialogService);
	public accAreaService = inject(AcceleratorAreaService);
	public util = inject(UtilityService);
	private domain = inject(DomainDataService)

	saveActionEnabled = signal(false);

	saveAction: DialogAction = {
		id: 'save',
		cssClass: signal('btn-primary'),
		enabled: this.saveActionEnabled,
		visible: signal(true),
		text: signal('Save'),
		linkType: 'callback',
		callback: this.save.bind(this),
		willCloseDialog: true,
	};

	cancelAction: DialogAction = {
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: "no-action",
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.saveAction, this.cancelAction];

	icon = signal('fa-regular fa-pencil');

	public isReadonly = computed(() => !this.accAreaService.canEdit());

	public topic = computed(() => {
		const data = this.accAreaService.data();
		if (!data || !this.dialog.data) return undefined;

		const accTopic = data.accTopics.find(t => t.accTopicId === this.dialog.data!.accTopicId);
		if (!accTopic) return undefined;

		const ddTopics = this.domain.data().topics;
		const topic = ddTopics.find(t => t.topicId === accTopic.topicId)!;
		const specialists = data.topicSpecialists.filter(ts => ts.accTopicId === accTopic.accTopicId);

		const facilitator = data.acc.facilitator;
		const specialistPersonIds = specialists.map(s => s.personId).sort((a, b) => a - b);


		return {
			accTopicId: accTopic.accTopicId,
			trimesterId: data.acc.trimesterId,
			topic: topic.name,
			needsSpecialist: topic.needsSpecialist,
			specialistPersonIds,
			facilitatorPersonId: facilitator?.personId,
			list: specialists.length ? specialists.map(s => ({
				personId: s.personId,
				personName: s.personName,
			})) : facilitator ? [{
				personId: facilitator?.personId || 1,
				personName: facilitator?.fullName || '',
			}] : [],
		};
	});


	public newSelectedSpecialistIds = signal<number[]>(this.topic()?.specialistPersonIds || []);


	private async save() {
		const topic = this.topic();
		if (!topic) return;

		// If there are specialists(other than facilitator) assigned to the topic and are being removed, show a confirmation dialog.
		const nonFacilitatorSpecialistIds = topic.specialistPersonIds.filter(id => id !== topic.facilitatorPersonId);
		const specialistsRemoved = nonFacilitatorSpecialistIds.length > this.newSelectedSpecialistIds().length;

		if (specialistsRemoved) {
			const yes = await this.dialogService.confirm('Are you sure you want to remove this Specialist? If so, remember to notify volunteering@eforall.org of this change', 'Yes', 'No', [], 300, 200);
			if (!yes) return;
		}

		await this.accAreaService.setTopicSpecialists(topic.accTopicId, topic.trimesterId, this.newSelectedSpecialistIds());

	}

	specialistsChange(e: number[]) {
		this.newSelectedSpecialistIds.set([...e].sort((a, b) => a - b));
		this.checkIfCanSave();
	}

	checkIfCanSave() {
		const newSelectedSpecialistIds = this.newSelectedSpecialistIds();
		const valuesAreSame = this.util.values.areSame(this.topic()?.specialistPersonIds || [], newSelectedSpecialistIds);
		const enabled = !this.isReadonly() && !valuesAreSame;

		this.saveActionEnabled.set(enabled);
	}
}
